import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { ArrowLeft, Mail, Shield } from 'lucide-react';
import api from '@/lib/axios';
import { handleError } from '@/lib/utils/error-handler';
import { useTranslation } from '@/contexts/TranslationContext';
import { Button } from '@/components/ui/button';
import { LDGrowthIcon } from '@/components/ui/LDGrowthIcon';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface ApiError {
  response?: {
    data?: {
      message?: string
    }
  }
}

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Ref for proper focus management
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Auto-focus email input on mount with proper React pattern
    if (emailRef.current) {
      emailRef.current.focus();
    }
  }, []);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setEmailError(t('auth.emailRequired'));
      return false;
    }
    if (!emailRegex.test(email.trim())) {
      setEmailError(t('auth.invalidEmail'));
      return false;
    }
    setEmailError('');
    return true;
  };

  // Real-time form validation
  useEffect(() => {
    const isEmailValid = !!email && validateEmail(email);
    setIsFormValid(isEmailValid);
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    const isEmailValid = validateEmail(email.trim());
    if (!isEmailValid) {
      if (emailRef.current) {
        emailRef.current.focus();
      }
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/api/auth/forgot-password', { email: email.trim() });
      const isSuccessfulSubmission = response?.data?.success === true;

      if (!isSuccessfulSubmission) {
        throw new Error(t('auth.resetPasswordError', 'Failed to reset password'));
      }

      setIsSubmitted(true);
      toast({
        title: t('common.success'),
        description: t('auth.passwordResetSent'),
        duration: 5000,
      });
    } catch (error: any) {
      const apiError = error as ApiError;
      setSubmitError(
        apiError?.response?.data?.message ||
        t('auth.resetPasswordError', 'Failed to reset password. Please try again.')
      );
      if (emailRef.current) {
        emailRef.current.focus();
        emailRef.current.select();
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[var(--ld-neutral-50)] via-white to-[var(--ld-primary-50)] flex items-center justify-center p-4 ld-bg-pattern">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[var(--ld-primary)] opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[var(--ld-primary)] opacity-3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 w-full max-w-md mx-auto ld-animate-slide-up">
        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <LDGrowthIcon size={48} className="ld-shadow-glow" />
            <h1 className="text-3xl font-bold ld-text-gradient">LD Growth</h1>
          </div>
          <p className="text-[var(--ld-neutral-600)] font-medium">Leadership Development Platform</p>
        </div>

        <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-2xl ld-shadow-glow">
          <CardContent className="p-8">
            {/* Form Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[var(--ld-primary)] rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--ld-neutral-800)] mb-2">
                {t('auth.resetPassword')}
              </h2>
              <p className="text-[var(--ld-neutral-600)]">
                Enter your email address and we'll send you a temporary password to log in.
              </p>
            </div>

            {!isSubmitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-semibold text-[var(--ld-neutral-700)]">
                    {t('auth.email')}
                  </label>
                  <div className="relative">
                    <input
                      ref={emailRef}
                      id="email"
                      type="email"
                      required
                      autoComplete="email"
                      className={`w-full h-14 px-4 pr-12 rounded-xl border-2 transition-all duration-300 bg-white/50 backdrop-blur-sm ${
                        emailError
                          ? 'border-[var(--ld-error)] focus:border-[var(--ld-error)] focus:ring-4 focus:ring-[var(--ld-error)]/20'
                          : email && !emailError
                          ? 'border-[var(--ld-success)] focus:border-[var(--ld-success)] focus:ring-4 focus:ring-[var(--ld-success)]/20'
                          : 'border-[var(--ld-neutral-200)] focus:border-[var(--ld-primary)] focus:ring-4 focus:ring-[var(--ld-primary)]/20'
                      } focus:outline-none placeholder:text-[var(--ld-neutral-400)] text-[var(--ld-neutral-800)] font-medium`}
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (e.target.value) validateEmail(e.target.value);
                      }}
                      onBlur={() => email && validateEmail(email)}
                    />
                    {email && !emailError && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        <div className="w-5 h-5 bg-[var(--ld-success)] rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </div>
                  {emailError && (
                    <p className="text-sm text-[var(--ld-error)] font-medium flex items-center gap-2 ld-animate-scale-in">
                      <span className="w-1 h-1 bg-[var(--ld-error)] rounded-full"></span>
                      {emailError}
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading || !isFormValid}
                  className={`w-full h-14 rounded-xl font-semibold text-base transition-all duration-300 ${
                    isFormValid && !isLoading
                      ? 'bg-[var(--ld-primary)] hover:bg-[var(--ld-primary-dark)] text-white shadow-lg hover:shadow-xl ld-shadow-glow transform hover:scale-[1.02]'
                      : 'bg-[var(--ld-neutral-200)] text-[var(--ld-neutral-500)] cursor-not-allowed'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-3">
                      <LoadingSpinner size="sm" color="white" />
                        <span>{t('auth.sending', 'Sending...')}</span>
                    </div>
                  ) : (
                    t('auth.sendResetInstructions', 'Send temporary password')
                  )}
                </Button>

                {submitError && (
                  <div className="rounded-xl border border-[var(--ld-error)]/30 bg-[var(--ld-error-light)] p-3 text-sm font-medium text-[var(--ld-error)]">
                    {submitError}
                  </div>
                )}
              </form>
            ) : (
              <div className="text-center space-y-6 ld-animate-scale-in">
                <div className="w-16 h-16 bg-[var(--ld-success)] rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-[var(--ld-neutral-800)]">Check your email</h3>
                  <p className="text-[var(--ld-neutral-600)]">
                    {t('auth.checkEmailForReset', 'Check your email for your temporary password. Use it to log in and then change your password.')}
                  </p>
                  <p className="text-sm text-[var(--ld-neutral-500)]">
                    {t('auth.didntReceiveEmail', 'Didn\'t receive the email? Check your spam folder or try again.')}
                  </p>
                </div>
                <Button
                  onClick={() => setIsSubmitted(false)}
                  variant="outline"
                  className="h-12 px-6 border-2 border-[var(--ld-primary)] text-[var(--ld-primary)] hover:bg-[var(--ld-primary)] hover:text-white transition-all duration-300"
                >
                  {t('auth.tryAgain', 'Try again')}
                </Button>
              </div>
            )}

            {/* Back to Login */}
            <div className="mt-8 pt-6 border-t border-[var(--ld-neutral-200)]">
              <Link
                to="/login"
                className="flex items-center justify-center gap-2 text-sm font-semibold text-[var(--ld-primary)] hover:text-[var(--ld-primary-dark)] transition-colors duration-200"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('auth.backToLogin', 'Back to login')}
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Security Badge */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full border border-white/20">
            <Shield className="w-4 h-4 text-[var(--ld-success)]" />
            <span className="text-sm font-medium text-[var(--ld-neutral-700)]">
              Secure password recovery
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}